import { List, ListItem, ListItemIcon, ListItemText, Paper, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { FC, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone/.';
import { useTranslation } from 'react-i18next';
import { UploadedFile } from 'types/uploadedFile';

interface FileUploaderProps {
    onZipRead: (fileContent: ArrayBuffer) => void;
}

const FileUploader: FC<FileUploaderProps> = (props) => {
    const { onZipRead } = props;
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const theme = useTheme();
    const { t } = useTranslation();

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles.length === 0) return;

            const file = acceptedFiles[0];
            const reader = new FileReader();

            reader.onload = async () => {
                try {
                    const content = reader.result as ArrayBuffer;
                    onZipRead(content);

                    setFiles((prev) => [
                        ...prev,
                        {
                            name: file.name,
                            size: file.size
                        }
                    ]);
                } catch (error) {
                    console.error('Error extracting zip file:', error);
                }
            };

            reader.onerror = () => {
                console.error('Error on reading the zip file');
            };

            reader.readAsArrayBuffer(file);
        },
        [onZipRead]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false,
        accept: { 'application/zip': ['.zip'] }
    });

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 4,
                borderStyle: 'dashed',
                textAlign: 'center',
                borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.secondary,
                bgcolor: theme.palette.background.default,
                color: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.secondary
            }}
        >
            <Box
                {...getRootProps()}
                sx={{
                    p: 5,
                    cursor: 'pointer',
                    bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                    borderRadius: 2,
                    transition: 'background-color 0.3s',
                    '&:hover': {
                        bgcolor: 'action.hover'
                    }
                }}
            >
                <input {...getInputProps()} />
                <Typography variant="body1">{isDragActive ? t('RELEASE_FILE_HERE') : t('LOAD_YOUR_FILE_HERE')}</Typography>
            </Box>

            {files.length > 0 && (
                <Box mt={4} textAlign="left">
                    <Typography variant="h6" gutterBottom>
                        {t('LOADED_FILES')}
                    </Typography>
                    <List>
                        {files.map((file, index) => (
                            <ListItem key={index}>
                                <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(2)} KB`} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
        </Paper>
    );
};

export default FileUploader;
